import React from 'react';
import { useTranslation } from 'react-i18next';

interface CustomDateFormatInputProps {
  show: boolean;
  customDateFormat: string;
  onFormatChange: (format: string) => void;
  onClose: () => void;
  onReset: () => void;
}

const CustomDateFormatInput: React.FC<CustomDateFormatInputProps> = ({
  show,
  customDateFormat,
  onFormatChange,
  onClose,
  onReset
}) => {
  const { t } = useTranslation('dataUpload');

  if (!show) return null;

  const formatExamples = [
    { format: '%Y-%m-%d %H:%M:%S', example: '2024-03-11 14:30:00' },
    { format: '%d.%m.%Y %H:%M', example: '11.03.2024 14:30' },
    { format: '%Y/%m/%d', example: '2024/03/11' },
    { format: '%d/%m/%Y %H:%M:%S', example: '11/03/2024 14:30:00' }
  ];

  return (
    <div className="relative mb-6 bg-muted p-6 rounded-xl shadow-md border border-border">
      {/* Close button */}
      <button
        className="absolute top-2 right-2 text-muted-foreground hover:text-foreground text-xl font-bold"
        onClick={onClose}
        aria-label="Close"
      >
        ×
      </button>

      <h3 className="text-lg font-semibold mb-3 text-foreground flex items-center gap-2">
        <span>{t('dateTimeFormat.customFormat.label')}</span>
      </h3>
      
      <div className="flex flex-col gap-2">
        <input
          type="text"
          value={customDateFormat}
          onChange={(e) => onFormatChange(e.target.value)}
          placeholder={t('dateTimeFormat.customFormat.placeholder')}
          className="w-full border border-input rounded-lg px-4 py-2 shadow-sm focus:ring-2 focus:ring-ring focus:outline-none"
        />
        
        <div className="text-muted-foreground text-sm">
          <p>{t('dateTimeFormat.customFormat.instructions', 'Geben Sie Ihr eigenes Datumsformat ein. Beispiele:')}</p>
          <ul className="mt-1 space-y-1 text-foreground">
            {formatExamples.map((item, index) => (
              <li key={index}>
                <code className="bg-muted px-2 py-1 rounded-md">{item.format}</code> → {item.example}
              </li>
            ))}
          </ul>
        </div>
    
        <p className="text-muted-foreground text-xs mt-2">
          <strong>{t('dateTimeFormat.customFormat.note', 'Hinweis')}:</strong>{' '}
          {t('dateTimeFormat.customFormat.noteText', 'Das Format muss einem unterstützten Datumsformat entsprechen.')}
        </p>
      </div>
    
      <div className="flex justify-end mt-4">
        <button
          onClick={onReset}
          className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-all shadow-md"
        >
          ✖️ {t('dateTimeFormat.customFormat.reset', 'Reset')}
        </button>
      </div>
    </div>
  );
};

export default CustomDateFormatInput;